import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import en from './locales/en';
import cs from './locales/cs';
import es from './locales/es';
import pt from './locales/pt';
import de from './locales/de';
import fr from './locales/fr';
import it from './locales/it';
import pl from './locales/pl';

export type LanguageOption = {
    code: string;
    label: string;
    nativeLabel: string;
    shortLabel: string;
    flag: string;
};

export const SUPPORTED_LANGUAGES: LanguageOption[] = [
    { code: 'en', label: 'English', nativeLabel: 'English', shortLabel: 'EN', flag: '🇺🇸' },
    { code: 'cs', label: 'Čeština', nativeLabel: 'Čeština', shortLabel: 'CZ', flag: '🇨🇿' },
    { code: 'es', label: 'Spanish', nativeLabel: 'Español', shortLabel: 'ES', flag: '🇪🇸' },
    { code: 'pt', label: 'Portuguese', nativeLabel: 'Português', shortLabel: 'PT', flag: '🇵🇹' },
    { code: 'de', label: 'German', nativeLabel: 'Deutsch', shortLabel: 'DE', flag: '🇩🇪' },
    { code: 'fr', label: 'French', nativeLabel: 'Français', shortLabel: 'FR', flag: '🇫🇷' },
    { code: 'it', label: 'Italian', nativeLabel: 'Italiano', shortLabel: 'IT', flag: '🇮🇹' },
    { code: 'pl', label: 'Polish', nativeLabel: 'Polski', shortLabel: 'PL', flag: '🇵🇱' },
];

export const resolveLanguageCode = (language: string): string => {
    const normalized = language.replace('_', '-');
    const exact = SUPPORTED_LANGUAGES.find(
        (option) => normalized === option.code || normalized.toLowerCase() === option.code.toLowerCase()
    );
    if (exact) return exact.code;

    // Legacy Brazilian tag → global Portuguese
    if (/^pt(-|$)/i.test(normalized)) {
        return 'pt';
    }

    const match = SUPPORTED_LANGUAGES.find(
        (option) =>
            normalized === option.code ||
            normalized.startsWith(`${option.code}-`) ||
            (option.code.includes('-') && normalized.toLowerCase().startsWith(option.code.split('-')[0].toLowerCase()))
    );

    return match?.code ?? SUPPORTED_LANGUAGES[0].code;
};

const resources = {
    en: { translation: en },
    cs: { translation: cs },
    es: { translation: es },
    pt: { translation: pt },
    de: { translation: de },
    fr: { translation: fr },
    it: { translation: it },
    pl: { translation: pl },
};

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        supportedLngs: SUPPORTED_LANGUAGES.map((language) => language.code),
        fallbackLng: 'en',
        nonExplicitSupportedLngs: true,
        interpolation: {
            escapeValue: false,
        },
        detection: {
            order: ['querystring', 'localStorage', 'navigator'],
            caches: ['localStorage'],
            convertDetectedLanguage: (language) => resolveLanguageCode(language),
        },
    });

const syncDocumentLanguage = (language: string) => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = resolveLanguageCode(language);
};

syncDocumentLanguage(i18n.language);
i18n.on('languageChanged', syncDocumentLanguage);

export default i18n;
